import { getChannel } from "~/utils/getChannel";
import { resolveRequestLang } from "~/utils/request-lang";
import { isPageBffDebugEnabled, logPageBff } from "~/utils/page-data/debug-page-bff";

type PageBffQuery = Record<string, string | number | boolean | undefined | null>;

type PageBffRequestOptions = {
	headers?: Record<string, string>;
	locale?: string | null;
	routePath?: string;
};

function getPagePath(fallbackPath: string) {
	if (import.meta.client && typeof window !== "undefined") {
		return window.location.pathname;
	}

	try {
		return useRequestURL().pathname;
	} catch {
		return fallbackPath;
	}
}

function normalizeExtraHeaders(headers: Record<string, string> = {}) {
	const normalized = { ...headers };

	if (!normalized.Lang && normalized.lang) {
		normalized.Lang = normalized.lang;
	}
	delete normalized.lang;

	return normalized;
}

function getPageBffHeaders(path: string, requestOptions: PageBffRequestOptions = {}) {
	const extraHeaders = normalizeExtraHeaders(requestOptions.headers);
	const headers: Record<string, string> = {};
	const lang =
		extraHeaders.Lang ||
		resolveRequestLang(requestOptions.routePath || getPagePath(path), {
			i18nLocale: requestOptions.locale,
		});

	headers.Lang = lang;

	if (import.meta.client && typeof window !== "undefined") {
		const token = localStorage.getItem("token");
		const visitorId = localStorage.getItem("USER_IDENT");
		const appSystem = localStorage.getItem("app_system");

		if (token) {
			headers.Authorization = token;
			headers["Yqw-Token"] = token;
		}

		headers.channel = getChannel();

		if (visitorId) {
			headers.visitor = visitorId;
		}
		if (appSystem) {
			headers.system = appSystem;
		}
	}

	return {
		...headers,
		...extraHeaders,
	};
}

export async function fetchPageBff<T>(
	path: string,
	query?: PageBffQuery,
	requestOptions: PageBffRequestOptions = {}
) {
	const headers = getPageBffHeaders(path, requestOptions);
	const debug = isPageBffDebugEnabled();
	if (debug) {
		headers["x-debug-page-bff"] = "1";
	}
	const options = { query, headers };

	if (debug) {
		logPageBff("request", { path, query, server: import.meta.server });
	}

	const data = import.meta.server
		? await useRequestFetch()<T>(path, options)
		: await $fetch<T>(path, options);

	if (debug) {
		logPageBff("response", { path, data });
	}

	return data;
}
